import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getAllLevels as getStaticLevels, isAdvancedModuleAvailable, isPublicModule } from '../data/kariyerCurriculum';
import { fetchAllLevels } from '../services/curriculumService';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Flame, Target, Trophy, Award, Settings, BookOpen, 
  ChevronRight, CheckCircle2, Clock, Star,
  LogOut, User, AlertCircle, Play, Zap, MessageSquare, Shield, Bell,
  Volume2, Layers, Timer
} from 'lucide-react';
import axios from 'axios';
import { cn } from '@/lib/utils';
import KariyerLogo, { KariyerLogoIcon } from '../components/KariyerLogo';
import { useDailyReminder } from '../hooks/useDailyReminder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function KariyerHome() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState({});
  const [dailyTask, setDailyTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inactivityWarning, setInactivityWarning] = useState(null);
  const [cabinReadiness, setCabinReadiness] = useState(0);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const [levels, setLevels] = useState([]);

  // Günlük hatırlatıcı hook'u
  const { requestNotificationPermission } = useDailyReminder(user, stats);

  useEffect(() => {
    fetchData();
    loadLevels();
    // Bildirim izni kontrolü
    if ('Notification' in window) {
      setNotificationEnabled(Notification.permission === 'granted');
    }
  }, []);

  const loadLevels = async () => {
    try {
      // Önce API'den dene
      const apiLevels = await fetchAllLevels('english');
      if (apiLevels && apiLevels.length > 0) {
        setLevels(apiLevels);
        return;
      }
    } catch (error) {
      console.log('API error, falling back to static');
    }
    // Fallback: Static dosyadan yükle
    setLevels(getStaticLevels());
  };

  const fetchData = async () => {
    try {
      const [statsRes, progressRes, dailyRes, cabinRes] = await Promise.all([
        axios.get(`${API}/kariyer/stats`, { withCredentials: true }),
        axios.get(`${API}/kariyer/progress`, { withCredentials: true }),
        axios.get(`${API}/kariyer/daily-task`, { withCredentials: true }),
        axios.get(`${API}/kariyer/cabin-readiness`, { withCredentials: true }).catch(() => ({ data: { cabin_readiness_score: 0 } }))
      ]);
      setStats(statsRes.data);
      setProgress(progressRes.data);
      setDailyTask(dailyRes.data);
      setCabinReadiness(cabinRes.data.cabin_readiness_score || 0);
      
      if (statsRes.data.inactive_days > 0) {
        setInactivityWarning(statsRes.data.inactive_days);
      }
    } catch (error) {
      console.error('Error:', error);
      setStats({ xp: 0, streak: 0, level: 0, today_minutes: 0, completed_levels: 0 });
      setProgress({ current_level: 0, overall_progress: 0 });
      setDailyTask({ completed: false, task: null });
      setCabinReadiness(0);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.log('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
    navigate('/');
  };

  const currentLevelIndex = progress?.current_level || 0;
  const dailyProgress = stats ? Math.min((stats.today_minutes / 10) * 100, 100) : 0;

  // Admin tarafından açılan seviyeler
  const unlockedLevels = progress?.unlocked_levels || user?.kariyer_progress?.unlocked_levels || [];
  const allUnlocked = progress?.all_unlocked || user?.kariyer_progress?.all_unlocked || false;

  // Kullanıcının seviyesine göre kilitleme mantığı
  // Ana seviyeler sırasıyla: A0 → A1 → A2 → B1 → B2 → C1 → C2
  const levelOrder = ['A0', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  
  // Progress API'den gelen current_level'a göre açık seviyeleri belirle
  // current_level: 0 = A0 tamamlandı, 1 = A1 tamamlandı, vs.
  // Bu değer, tamamlanmış en yüksek seviyenin bir üstünü gösteriyor
  const progressBasedLevel = currentLevelIndex; // 0, 1, 2, ... 7
  
  // Level ID'den level code'a dönüştür
  const getLevelCodeFromId = (levelId) => {
    const mapping = {
      'level_0': 'A0', 'level_1': 'A1', 'level_2': 'A2', 
      'level_3': 'B1', 'level_4': 'B1-INT', 'level_5': 'B2',
      'level_6': 'B2-INT', 'level_7': 'C1', 'level_8': 'C1-INT'
    };
    return mapping[levelId] || levelId;
  };
  
  // Seviyenin kilitli olup olmadığını kontrol et
  const isLevelLocked = (levelCode, levelId) => {
    if (!levelCode) return false;
    
    // Admin tüm seviyeleri açtıysa hiçbiri kilitli değil
    if (allUnlocked) return false;
    
    // Admin bu seviyeyi özellikle açtıysa kilitli değil
    if (levelId && unlockedLevels.includes(levelId)) return false;
    
    const code = levelCode.toUpperCase();
    
    // CCT (Cabin Crew Training) özel modül - her zaman açık
    if (code === 'CCT' || code === 'INT') return false;
    
    // Level ID'den seviye numarasını çıkar (level_0 -> 0, level_1 -> 1, vs.)
    if (levelId) {
      const levelNum = parseInt(levelId.replace('level_', ''));
      // progressBasedLevel, tamamlanmış en yüksek seviyenin bir sonrasını gösterir
      // Yani current_level = 1 ise, level_0 tamamlanmış, level_1 açık
      // level_0 ve level_1 açık olmalı (levelNum <= progressBasedLevel)
      if (!isNaN(levelNum)) {
        return levelNum > progressBasedLevel;
      }
    }
    
    // Fallback: Ana seviyeler için standart kontrol
    const levelIdx = levelOrder.indexOf(code);
    if (levelIdx === -1) return false;
    
    return levelIdx > progressBasedLevel;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 to-blue-600 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-bounce text-6xl mb-4">✈️</div>
          <p className="text-white font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header - Mavi Gradient */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 pt-6 pb-8 rounded-b-[2rem]">
        <div className="max-w-4xl mx-auto">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/dashboard')}
                className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg hover:bg-gray-50 transition-colors"
              >
                <KariyerLogoIcon size={28} />
              </button>
              <div>
                <h1 className="text-white font-bold text-xl">
                  <span style={{ color: '#fff' }}>İngilizce</span>
                  <span style={{ color: '#a7f3d0' }}> Eğitimi</span>
                </h1>
                <p className="text-blue-100 text-xs">Havacılık İngilizcesi A0-C1</p>
              </div>
            </div>
            
            {/* Stats Pills */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-2 rounded-full">
                <Flame className="w-5 h-5 text-orange-300" />
                <span className="text-white font-bold">{stats?.streak || 0}</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur px-3 py-2 rounded-full">
                <Zap className="w-5 h-5 text-yellow-300" />
                <span className="text-white font-bold">{stats?.miles || 0}</span>
              </div>
            </div>
          </div>

          {/* Welcome Card */}
          <div className="bg-white rounded-2xl p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Hoş geldin 👋</p>
                <h2 className="text-xl font-bold text-gray-800">
                  {user?.first_name || user?.name?.split(' ')[0] || 'Öğrenci'}
                </h2>
              </div>
              <button 
                onClick={() => navigate('/profile')}
                className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"
              >
                <User className="w-6 h-6 text-blue-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* İnaktivite Uyarısı */}
        {inactivityWarning && inactivityWarning >= 3 && (
          <Card className="mb-4 border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-orange-700 font-medium">
                    {inactivityWarning} gündür çalışmadın!
                  </p>
                  <p className="text-orange-600 text-sm">
                    Günlük 10 dakika çalışarak hedefe ulaş.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Günlük Görev Kartı */}
        <Card className="mb-6 border-0 shadow-lg bg-white overflow-hidden">
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                <span className="font-semibold">Günlük Hedef</span>
              </div>
              <span className="font-bold text-lg">
                {stats?.today_minutes || 0}/10 dk
              </span>
            </div>
            <Progress value={dailyProgress} className="h-2 mt-3 bg-white/30" />
          </div>
          <CardContent className="p-4">
            {dailyProgress >= 100 ? (
              <div className="flex items-center gap-3 text-green-600">
                <CheckCircle2 className="w-6 h-6" />
                <span className="font-medium">Bugünkü hedefini tamamladın! 🎉</span>
              </div>
            ) : dailyTask?.task ? (
              <div className="space-y-3">
                <Button
                  onClick={() => navigate(`/lesson/${dailyTask.task.level_id}/${dailyTask.task.unit_id}/${dailyTask.task.lesson_id}`)}
                  className="w-full py-6 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-semibold text-lg shadow-lg"
                >
                  <Play className="w-6 h-6 mr-2" />
                  {dailyTask.task.title}
                </Button>
                {/* Bildirim Butonu */}
                {!notificationEnabled && 'Notification' in window && (
                  <button
                    onClick={async () => {
                      const granted = await requestNotificationPermission();
                      setNotificationEnabled(granted);
                    }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-blue-600 py-2"
                  >
                    <Bell className="w-4 h-4" />
                    <span>Günlük hatırlatıcı bildirimi aç</span>
                  </button>
                )}
                {notificationEnabled && (
                  <div className="flex items-center justify-center gap-2 text-sm text-green-600">
                    <Bell className="w-4 h-4" />
                    <span>Hatırlatıcı bildirimleri açık ✓</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center">Bir seviye seçerek başla!</p>
            )}
          </CardContent>
        </Card>

        {/* İnteraktif Uygulamalar */}
        <Card className="mb-6 border-0 shadow-lg bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardContent className="p-5">
            <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
              🎮 İnteraktif Uygulamalar
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Eğlenceli ve etkileşimli pratik araçları
            </p>
            <div className="grid grid-cols-4 gap-3">
              <button
                onClick={() => navigate('/cabin-crew/voice-practice')}
                className="flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-xl bg-white hover:bg-purple-50 border-2 border-purple-200 hover:border-purple-400 text-purple-700 font-semibold transition-all shadow-sm"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Volume2 className="w-6 h-6 text-purple-600" />
                </div>
                <span className="text-xs text-center">Sesli Pratik</span>
              </button>
              <button
                onClick={() => navigate('/cabin-crew/roleplay')}
                className="flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-xl bg-white hover:bg-blue-50 border-2 border-blue-200 hover:border-blue-400 text-blue-700 font-semibold transition-all shadow-sm"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-xs text-center">Rol Yapma</span>
              </button>
              <button
                onClick={() => navigate('/cabin-crew/flashcards')}
                className="flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-xl bg-white hover:bg-amber-50 border-2 border-amber-200 hover:border-amber-400 text-amber-700 font-semibold transition-all shadow-sm"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Layers className="w-6 h-6 text-amber-600" />
                </div>
                <span className="text-xs text-center">Flashcards</span>
              </button>
              <button
                onClick={() => navigate('/cabin-crew/timed-challenge')}
                className="flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-xl bg-white hover:bg-red-50 border-2 border-red-200 hover:border-red-400 text-red-700 font-semibold transition-all shadow-sm"
              >
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Timer className="w-6 h-6 text-red-600" />
                </div>
                <span className="text-xs text-center">Zamanlı Yarışma</span>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Seviye Haritası */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-500" />
              Seviye Haritası
            </h3>
            <div className="flex items-center gap-2">
              {user?.current_level && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                  Seviye: {user.current_level}
                </span>
              )}
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {currentLevelIndex}/{levels.length}
              </span>
            </div>
          </div>
          
          <div className="space-y-3">
            {levels.map((level, index) => {
              const isAdvanced = level.level_code?.includes('INT') || level.level_code === 'CCT';
              const isPublic = level.isPublic === true;
              
              // Kullanıcının seviyesine göre kilitleme (level.id'yi de gönder)
              const isLocked = isLevelLocked(level.level_code, level.id);
              const isCompleted = index < currentLevelIndex;
              const isCurrent = !isLocked && index === currentLevelIndex;
              const levelProgress = progress?.level_progress?.[level.id] || 0;
              
              return (
                <Card 
                  key={level.id}
                  className={cn(
                    'border-0 shadow-md transition-all overflow-hidden',
                    isLocked 
                      ? 'bg-gray-100 opacity-60 cursor-not-allowed' 
                      : 'bg-white hover:shadow-lg hover:scale-[1.02] cursor-pointer',
                    isAdvanced && !isLocked ? 'ring-2 ring-amber-400 bg-gradient-to-r from-amber-50 to-yellow-50' : '',
                    isPublic && !isLocked ? 'ring-2 ring-sky-400 bg-gradient-to-r from-sky-50 to-blue-50' : ''
                  )}
                  onClick={() => !isLocked && navigate(`/level/${level.id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className={cn(
                        'w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md',
                        isLocked ? 'bg-gray-200' :
                        isCompleted ? 'bg-green-100' : 
                        isCurrent ? `bg-gradient-to-br ${level.color}` :
                        isAdvanced ? 'bg-gradient-to-br from-amber-400 to-yellow-500' :
                        `bg-gradient-to-br ${level.color}`
                      )}>
                        {isLocked ? (
                          <Shield className="w-8 h-8 text-gray-400" />
                        ) : isCompleted ? (
                          <CheckCircle2 className="w-8 h-8 text-green-500" />
                        ) : (
                          level.icon
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className={cn(
                            "font-bold",
                            isLocked ? "text-gray-400" : "text-gray-800"
                          )}>
                            {level.level_code === 'INT' ? level.title : `Seviye ${level.level_code}: ${level.title}`}
                          </h4>
                          {level.level_code !== 'INT' && (
                            <span className={cn(
                              'text-xs px-2 py-0.5 rounded-full font-medium',
                              isLocked ? 'bg-gray-200 text-gray-400' :
                              isCompleted ? 'bg-green-100 text-green-600' :
                              isAdvanced ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-600'
                            )}>
                              {level.level_code}
                            </span>
                          )}
                          {isLocked && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-200 text-gray-500">
                              🔒 Kilitli
                            </span>
                          )}
                          {isAdvanced && !isLocked && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                              👑 Özel
                            </span>
                          )}
                        </div>
                        <p className={cn(
                          'text-sm',
                          isLocked ? 'text-gray-400' : 'text-gray-500'
                        )}>
                          {isLocked 
                            ? `Önce önceki seviyeyi tamamlayın`
                            : (level.titleTr || level.description || `${level.units?.length || 0} ünite`)
                          }
                        </p>
                        
                        {!isLocked && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                              <span>{level.units?.length || 0} ünite</span>
                              <span>{Math.round(levelProgress)}%</span>
                            </div>
                            <div className="h-1.5 bg-blue-100 rounded-full overflow-hidden">
                              <div 
                                className={cn(
                                  "h-full rounded-full transition-all",
                                  isAdvanced 
                                    ? "bg-gradient-to-r from-amber-400 to-yellow-500"
                                    : "bg-gradient-to-r from-cyan-400 to-blue-500"
                                )}
                                style={{ width: `${levelProgress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Arrow */}
                      <ChevronRight className={cn(
                        "w-5 h-5",
                        isLocked ? "text-gray-300" : "text-gray-400"
                      )} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Çıkış */}
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Çıkış Yap
        </Button>
      </div>

      {/* Alt Navigasyon - Beyaz */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex justify-around py-3">
            <button className="flex flex-col items-center p-2">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs mt-1 text-blue-600 font-medium">Öğren</span>
            </button>
            <button 
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center p-2"
            >
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-gray-500" />
              </div>
              <span className="text-xs mt-1 text-gray-500">Profil</span>
            </button>
            <button 
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center p-2"
            >
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-gray-500" />
              </div>
              <span className="text-xs mt-1 text-gray-500">Ayarlar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
